/**
 * DocumentComments — a Facebook-style comment thread for a document
 * (file, text message, or voice message — all the unified Document model).
 *
 * Any workspace member who can access the document may read and post text
 * comments. Each comment shows the author, a relative timestamp, and (when
 * allowed) a delete button. Posting notifies the uploader + prior commenters
 * server-side; this component just renders + mutates the thread.
 *
 * Workspace switching is handled by the api interceptor (it injects
 * owner_account_id automatically), so callers don't pass it here.
 *
 * Props:
 *   - documentId
 *   - currentUserId   (to render "You" and decide optimistic author name)
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { ar as arLocale } from 'date-fns/locale'
import { HiOutlineChatBubbleOvalLeft, HiOutlineTrash, HiOutlinePaperAirplane } from 'react-icons/hi2'
import { documentAPI } from '../../services/api'
import { useDirection } from '../../contexts/DirectionContext'
import { useAlerts } from '../../contexts/AlertContext'
import { Card, Button } from '../ui'
import ConfirmDialog from '../ui/ConfirmDialog'

function initials(name) {
  if (!name) return '?'
  const parts = String(name).trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?'
}

function unwrap(res) {
  return res?.data?.data ?? res?.data ?? null
}

export default function DocumentComments({ documentId, currentUserId }) {
  const { t } = useTranslation()
  const { isRtl } = useDirection()
  const { showError } = useAlerts()

  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [content, setContent] = useState('')
  const [posting, setPosting] = useState(false)
  const [confirm, setConfirm] = useState({ open: false, commentId: null })

  const relTime = useCallback((iso) => {
    if (!iso) return ''
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: isRtl ? arLocale : undefined })
    } catch {
      return ''
    }
  }, [isRtl])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await documentAPI.getComments(documentId)
      if (res.success) {
        const data = unwrap(res)
        setComments(Array.isArray(data) ? data : [])
      }
    } finally {
      setLoading(false)
    }
  }, [documentId])

  useEffect(() => { load() }, [load])

  const post = async () => {
    const text = content.trim()
    if (!text || posting) return
    setPosting(true)
    try {
      const res = await documentAPI.postComment(documentId, text)
      if (res.success) {
        const created = unwrap(res)
        if (created) setComments((prev) => [...prev, created])
        setContent('')
      } else {
        showError(res.error?.message || t('documents.detail.comments.postFailed'))
      }
    } catch {
      showError(t('documents.detail.comments.postFailed'))
    } finally {
      setPosting(false)
    }
  }

  const doDelete = async () => {
    const commentId = confirm.commentId
    setConfirm({ open: false, commentId: null })
    if (!commentId) return
    // Optimistic removal.
    const prev = comments
    setComments((c) => c.filter((x) => x.id !== commentId))
    const res = await documentAPI.deleteComment(documentId, commentId)
    if (!res.success) {
      setComments(prev) // rollback
      showError(res.error?.message || t('documents.detail.comments.deleteFailed'))
    }
  }

  const onKeyDown = (e) => {
    // Ctrl/Cmd + Enter posts.
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault()
      post()
    }
  }

  return (
    <Card padding="md">
      <Card.Header
        title={
          <span className="inline-flex items-center gap-2">
            <HiOutlineChatBubbleOvalLeft className="w-5 h-5 text-brand" />
            {t('documents.detail.comments.title')}
            {comments.length > 0 && (
              <span className="text-xs font-normal text-ink-muted">({comments.length})</span>
            )}
          </span>
        }
      />

      {/* Composer */}
      <div className="flex items-start gap-2 mb-4">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={onKeyDown}
          dir="auto"
          rows={2}
          placeholder={t('documents.detail.comments.addPlaceholder')}
          className="flex-1 px-3 py-2 text-sm bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-y min-h-[44px] text-ink-primary placeholder:text-ink-muted"
        />
        <Button
          size="sm"
          onClick={post}
          loading={posting}
          disabled={!content.trim() || posting}
          leadingIcon={!posting ? HiOutlinePaperAirplane : undefined}
        >
          {t('documents.detail.comments.post')}
        </Button>
      </div>

      {/* Thread */}
      {loading ? (
        <p className="text-sm text-ink-muted py-4 text-center">{t('common.loading')}</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-ink-muted py-6 text-center">{t('documents.detail.comments.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => {
            const isMine = c.author === currentUserId
            return (
              <li key={c.id} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-brand-soft text-brand flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {initials(c.author_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="bg-muted rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-ink-primary truncate">
                        {isMine ? t('common.you') : c.author_name}
                      </span>
                      {c.can_delete && (
                        <button
                          type="button"
                          onClick={() => setConfirm({ open: true, commentId: c.id })}
                          className="text-ink-muted hover:text-danger transition-colors flex-shrink-0"
                          aria-label={t('common.delete')}
                          title={t('common.delete')}
                        >
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-ink-secondary whitespace-pre-wrap break-words" dir="auto">
                      {c.content}
                    </p>
                  </div>
                  <span className="text-[11px] text-ink-muted ms-1">{relTime(c.created_at)}</span>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <ConfirmDialog
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, commentId: null })}
        onConfirm={doDelete}
        title={t('documents.detail.comments.deleteTitle')}
        message={t('documents.detail.comments.deleteConfirm')}
        confirmText={t('common.delete')}
        type="danger"
      />
    </Card>
  )
}
